import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { from, issuerLabel, postgrestSource, rpc, unwrap, useAction, useHasRole, useIssuers, useOrg, type PersonLite } from "@vixio/data";
import { PersonPicker } from "@vixio/core-ui";
import {
  Button, Callout, Checkbox, ConfirmDialog, DataTable, DateInput, DescriptionList, Dialog, DocInput, KpiRow, KpiTile, MoneyInput, PageHeader,
  Section, Select, StatusFromMap, TextArea, TextInput, Timeline, formatDateTime, formatDoc, formatInt, formatMoney, formatMonth,
  formatPct, monthStartIso, todayIso,
} from "@vixio/ui";
import { FISCAL_PROVIDERS, INVOICE_STATUS, type InvoiceFull, type InvoiceView } from "./api";

export function useFiscalKpis(orgId: string) {
  const month = monthStartIso(todayIso());
  return useQuery({
    queryKey: ["fiscal", "kpis", orgId, month],
    queryFn: async () => {
      const rows = await unwrap<Array<{ status: string; amount_cents: number; issued_at: string | null }>>(
        from("fiscal", "v_invoices").select("status, amount_cents, issued_at").eq("org_id", orgId).limit(5000),
      );
      const issuedMonth = rows.filter((r) => r.status === "issued" && r.issued_at && r.issued_at >= month);
      return {
        issuedMonthCents: issuedMonth.reduce((a, r) => a + r.amount_cents, 0),
        issuedMonthCount: issuedMonth.length,
        queue: rows.filter((r) => r.status === "queued" || r.status === "processing").length,
        rejected: rows.filter((r) => r.status === "rejected").length,
        drafts: rows.filter((r) => r.status === "draft").length,
      };
    },
  });
}

export function InvoicesPage() {
  const org = useOrg();
  const issuers = useIssuers(org.id);
  const kpis = useFiscalKpis(org.id);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const issuerName = new Map((issuers.data ?? []).map((i) => [i.id, issuerLabel(i)]));
  const source = useMemo(
    () => postgrestSource<InvoiceView>({ schema: "fiscal", table: "v_invoices", orgId: org.id, searchColumns: ["taker_name", "description", "number"], tiebreak: "id" }),
    [org.id],
  );
  return (
    <div className="vx-content">
      <PageHeader
        title="Notas fiscais (NFS-e)"
        description="Rascunho com ISS e retenções calculados no banco, fila de emissão, consulta e cancelamento. Emitida é imutável."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
            Nova nota
          </Button>
        }
      />
      <KpiRow>
        <KpiTile label="Emitido no mês" value={formatMoney(kpis.data?.issuedMonthCents ?? 0)} foot={`${formatInt(kpis.data?.issuedMonthCount ?? 0)} nota(s)`} />
        <KpiTile label="Na fila" value={formatInt(kpis.data?.queue ?? 0)} />
        <KpiTile label="Rejeitadas" value={formatInt(kpis.data?.rejected ?? 0)} tone={(kpis.data?.rejected ?? 0) > 0 ? "crit" : undefined} foot="corrija e reenvie" />
        <KpiTile label="Rascunhos" value={formatInt(kpis.data?.drafts ?? 0)} />
      </KpiRow>
      <div className="vx-section">
        <DataTable<InvoiceView>
          caption="Notas fiscais"
          source={source}
          rowKey={(i) => i.id}
          onRowClick={(i) => setOpen(i.id)}
          initialSort={{ key: "created_at", dir: "desc" }}
          searchPlaceholder="Buscar por tomador, descrição ou número"
          exportName="notas-fiscais"
          filters={[
            { key: "status", label: "Situação", options: Object.entries(INVOICE_STATUS).map(([value, s]) => ({ value, label: s.label })) },
            { key: "issuer_id", label: "CNPJ", all: "Todos os CNPJs", options: (issuers.data ?? []).map((i) => ({ value: i.id, label: issuerLabel(i) })) },
          ]}
          columns={[
            { key: "number", header: "Número", sortable: true, cell: (i) => <span className="mono">{i.number ?? "-"}</span> },
            { key: "taker_name", header: "Tomador", sortable: true, cell: (i) => <span className="vx-table__primary">{i.taker_name ?? <span className="muted">sem tomador</span>}<span className="vx-table__sub">{i.description}</span></span> },
            { key: "issuer_id", header: "Emissor", cell: (i) => issuerName.get(i.issuer_id) ?? "-", csv: (i) => issuerName.get(i.issuer_id) ?? "" },
            { key: "competence", header: "Competência", cell: (i) => formatMonth(i.competence), csv: (i) => formatMonth(i.competence), sortable: true },
            { key: "amount_cents", header: "Valor", kind: "money", sortable: true },
            { key: "iss_cents", header: "ISS", kind: "money" },
            { key: "net_cents", header: "Líquido", kind: "money" },
            { key: "status", header: "Situação", cell: (i) => <StatusFromMap map={INVOICE_STATUS} value={i.status} />, csv: (i) => INVOICE_STATUS[i.status]?.label ?? i.status },
            { key: "issued_at", header: "Emitida em", kind: "date", sortable: true },
          ]}
        />
      </div>
      <NewInvoiceDialog open={creating} onOpenChange={setCreating} onCreated={setOpen} />
      <InvoiceSheet id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function NewInvoiceDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void }) {
  const org = useOrg();
  const issuers = useIssuers(org.id);
  const [person, setPerson] = useState<PersonLite | null>(null);
  const [f, setF] = useState({ issuer: "", name: "", doc: "", docOk: false, email: "", description: "", amount: null as number | null, competence: monthStartIso(todayIso()), serviceCode: "", issRetained: false });
  useEffect(() => {
    if (open && !f.issuer && issuers.data?.[0]) setF((x) => ({ ...x, issuer: issuers.data!.find((i) => i.active)?.id ?? "" }));
  }, [open, issuers.data, f.issuer]);
  useEffect(() => {
    if (person) setF((x) => ({ ...x, name: x.name || person.company_name || person.full_name, email: x.email || person.email || "" }));
  }, [person]);
  const create = useAction(
    () =>
      rpc("fiscal", "create_invoice", {
        p_org: org.id,
        p_issuer: f.issuer,
        p_taker: { name: f.name.trim() || undefined, doc: f.doc || undefined, email: f.email.trim() || undefined },
        p_description: f.description.trim(),
        p_amount_cents: f.amount ?? 0,
        p_competence: f.competence,
        p_person: person?.id ?? null,
        p_service_code: f.serviceCode.trim() || null,
        p_iss_retained: f.issRetained,
      }),
    {
      invalidate: [["fiscal"]],
      success: "Rascunho criado com ISS e retenções calculados",
      onSuccess: (id) => {
        onOpenChange(false);
        onCreated(id);
      },
    },
  );
  const ok = f.issuer && f.description.trim().length >= 10 && f.amount && f.amount > 0 && (f.doc === "" || f.docOk);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Nova nota fiscal"
      description="Cria um rascunho. Para ir à fila, o tomador precisa de nome e CPF/CNPJ e a discriminação, de 10 caracteres ou mais."
      onSubmit={() => ok && create.mutate(undefined)}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!ok}>
            Criar rascunho
          </Button>
        </>
      }
    >
      <div className="vx-form-grid">
        <Select className="span-2" label="CNPJ emissor" value={f.issuer} onChange={(v) => setF({ ...f, issuer: v })} placeholder="Escolha" options={(issuers.data ?? []).filter((i) => i.active).map((i) => ({ value: i.id, label: `${issuerLabel(i)} · ${formatDoc(i.cnpj)}` }))} />
        <PersonPicker label="Pessoa (opcional)" value={person} onChange={setPerson} />
        <TextInput label="Tomador (nome ou razão social)" value={f.name} onChange={(v) => setF({ ...f, name: v })} />
        <DocInput label="CPF ou CNPJ do tomador" value={f.doc} onChange={(v, valid) => setF({ ...f, doc: v, docOk: valid })} />
        <TextInput label="E-mail do tomador" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} />
        <TextArea className="span-2" label="Discriminação do serviço" value={f.description} onChange={(v) => setF({ ...f, description: v })} rows={3} required hint="Mínimo de 10 caracteres." />
        <MoneyInput label="Valor do serviço" value={f.amount} onChange={(c) => setF({ ...f, amount: c })} required />
        <DateInput label="Competência" type="month" value={f.competence} onChange={(v) => setF({ ...f, competence: v })} />
        <TextInput label="Código de serviço municipal" value={f.serviceCode} onChange={(v) => setF({ ...f, serviceCode: v })} hint="Vazio: o do emissor." />
        <div style={{ alignSelf: "end" }}>
          <Checkbox label="ISS retido pelo tomador" checked={f.issRetained} onChange={(v) => setF({ ...f, issRetained: v })} />
        </div>
      </div>
    </Dialog>
  );
}

const RET_LABEL: Record<string, string> = { ir: "IRRF", pis: "PIS", cofins: "COFINS", csll: "CSLL", inss: "INSS", iss_retido: "ISS retido" };

function InvoiceSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const issuers = useIssuers(org.id);
  const [canceling, setCanceling] = useState(false);
  const [manual, setManual] = useState(false);
  const inv = useQuery({
    queryKey: ["fiscal", "invoice", id],
    enabled: Boolean(id),
    queryFn: () => unwrap<InvoiceFull>(from("fiscal", "invoices").select("*").eq("id", id!).single()),
  });
  const events = useQuery({
    queryKey: ["fiscal", "invoice_events", id],
    enabled: Boolean(id),
    queryFn: () => unwrap<Array<{ id: number; kind: string; status_from: string | null; status_to: string | null; created_at: string; payload: Record<string, unknown> }>>(
      from("fiscal", "invoice_events").select("id, kind, status_from, status_to, created_at, payload").eq("invoice_id", id!).order("id", { ascending: false }),
    ),
  });
  const queue = useAction(() => rpc("fiscal", "queue_invoice", { p_invoice: id! }), { invalidate: [["fiscal"]], success: "Nota na fila de emissão" });
  const cancel = useAction((reason: string) => rpc("fiscal", "cancel_invoice", { p_invoice: id!, p_reason: reason }), {
    invalidate: [["fiscal"]],
    success: (s) => (s === "canceled" ? "Nota cancelada" : "Cancelamento pedido ao provedor"),
  });
  const i = inv.data;
  const issuer = issuers.data?.find((x) => x.id === i?.issuer_id);
  const rets = Object.entries(i?.retentions ?? {}).filter(([k, v]) => RET_LABEL[k] && typeof v === "number" && v > 0) as Array<[string, number]>;
  return (
    <Dialog sheet open={id !== null} onOpenChange={(o) => !o && onClose()} title={i ? (i.number ? `NFS-e ${i.number}` : "Nota em rascunho") : "Nota"} description={i?.taker?.name as string | undefined}>
      {!i ? (
        <p className="muted">Carregando</p>
      ) : (
        <div className="vx-stack vx-stack--lg">
          <div className="vx-row">
            <StatusFromMap map={INVOICE_STATUS} value={i.status} />
            {i.environment === "homologacao" ? <span className="vx-tag">homologação</span> : null}
            {i.provider ? <span className="text-2">{FISCAL_PROVIDERS[i.provider as keyof typeof FISCAL_PROVIDERS] ?? i.provider}</span> : null}
          </div>
          {i.status === "rejected" ? <Callout tone="crit" title="Rejeitada">{i.rejection_reason}</Callout> : null}
          {i.last_error && i.status === "processing" ? <Callout tone="warn" title={`Tentativa ${i.attempts}`}>{i.last_error}</Callout> : null}
          {i.status === "canceled" || i.status === "cancel_requested" ? <Callout tone="warn" title="Cancelamento">{i.cancel_reason}</Callout> : null}
          <DescriptionList
            items={[
              ["Emissor", issuer ? `${issuerLabel(issuer)} · ${formatDoc(issuer.cnpj)}` : "-"],
              ["Tomador", `${(i.taker?.name as string) ?? "-"}${i.taker?.doc ? ` · ${formatDoc(i.taker.doc as string)}` : ""}`],
              ["Discriminação", <span style={{ whiteSpace: "pre-wrap" }}>{i.description}</span>],
              ["Competência", formatMonth(i.competence)],
              ["Item LC 116", i.lc116_item],
              ["Valor", <span className="num">{formatMoney(i.amount_cents)}</span>],
              ["ISS", <span className="num">{formatMoney(i.iss_cents)} ({formatPct(Number(i.iss_aliquot_pct))}){i.iss_retained ? " retido" : ""}</span>],
              ...rets.map(([k, v]) => [RET_LABEL[k]!, <span className="num">{formatMoney(v)}</span>] as [string, JSX.Element]),
              ["Líquido a receber", <strong className="num">{formatMoney(i.net_cents)}</strong>],
              ["Emitida em", formatDateTime(i.issued_at)],
              ["RPS", i.rps_number ? <span className="mono">{`${i.rps_series ?? ""} ${i.rps_number}`}</span> : null],
              ["Documentos", i.pdf_url ? <a href={i.pdf_url} target="_blank" rel="noreferrer">PDF <ExternalLink width={12} aria-hidden="true" /></a> : null],
            ]}
          />
          <div className="vx-row">
            {i.status === "draft" ? (
              <Button variant="primary" onClick={() => queue.mutate(undefined)} loading={queue.isPending}>
                Enviar para a fila
              </Button>
            ) : null}
            {isAdmin && i.status === "draft" ? <Button onClick={() => setManual(true)}>Registrar emissão manual</Button> : null}
            {isAdmin && ["draft", "rejected", "queued", "issued"].includes(i.status) ? (
              <Button variant="danger" onClick={() => setCanceling(true)}>
                Cancelar nota
              </Button>
            ) : null}
          </div>
          <Section title="Trilha" id="trilha">
            <Timeline
              items={(events.data ?? []).map((e) => ({
                id: e.id,
                title: `${e.kind}${e.status_to ? `: ${INVOICE_STATUS[e.status_to]?.label ?? e.status_to}` : ""}`,
                meta: formatDateTime(e.created_at),
                tone: e.status_to === "issued" ? "ok" : e.status_to === "rejected" ? "crit" : "neutral",
              }))}
            />
          </Section>
        </div>
      )}
      <ConfirmDialog
        open={canceling}
        onOpenChange={setCanceling}
        title="Cancelar nota fiscal"
        confirmLabel="Cancelar nota"
        destructive
        confirmText="CANCELAR"
        reason={{ label: "Justificativa (vai para a prefeitura)", min: 15, max: 1000 }}
        onConfirm={(reason) => cancel.mutateAsync(reason)}
      >
        <Callout tone="warn">
          Emitida: o pedido vai ao provedor e a nota fica em cancelamento até a resposta. Rascunho ou fila: cancela na hora. Exige verificação em duas etapas.
        </Callout>
      </ConfirmDialog>
      {i ? <ManualIssueDialog id={i.id} open={manual} onOpenChange={setManual} /> : null}
    </Dialog>
  );
}

function ManualIssueDialog({ id, open, onOpenChange }: { id: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [number, setNumber] = useState("");
  const [code, setCode] = useState("");
  const [pdf, setPdf] = useState("");
  const save = useAction(() => rpc("fiscal", "record_manual_issue", { p_invoice: id, p_number: number.trim(), p_verification_code: code.trim() || null, p_pdf_url: pdf.trim() || null }), {
    invalidate: [["fiscal"]],
    success: "Emissão manual registrada",
    onSuccess: () => onOpenChange(false),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Registrar emissão manual"
      description="Para nota emitida fora do sistema (portal da prefeitura, contingência). Vira emitida e publica o evento para receita e contábil."
      onSubmit={() => number.trim() && save.mutate(undefined)}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!number.trim()}>
            Registrar
          </Button>
        </>
      }
    >
      <div className="vx-form-grid">
        <TextInput label="Número da nota" value={number} onChange={setNumber} required />
        <TextInput label="Código de verificação" value={code} onChange={setCode} />
        <TextInput className="span-2" label="Link do PDF (https)" value={pdf} onChange={setPdf} />
      </div>
    </Dialog>
  );
}


